import { action, computed, makeObservable } from 'mobx';

import { ComponentLoadingStore } from 'stores/componentLoadingStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import NewListModel from 'shared/models/NewListModel';
import { AgentPlatformTokenModel } from 'shared/models/agentPlatform';
import { apiUrls } from 'shared/entities/domain';
import {
  AgentPlatformTokenServer,
  CreateAgentPlatformTokenError,
  mapCreateAgentPlatformTokenErrorToMessage
} from 'shared/entities/agentPlatform';
import { validateIsEmpty } from 'shared/entities/validator';
import { FieldModel } from 'shared/models/form';

export default class AgentPlatformStore extends ComponentLoadingStore {
  private rootStore: IRootStore;
  readonly tokens: NewListModel<AgentPlatformTokenModel> =
    new NewListModel<AgentPlatformTokenModel>();
  readonly nameField: FieldModel = new FieldModel('', [validateIsEmpty]);
  readonly tokenField: FieldModel = new FieldModel('', [validateIsEmpty]);

  constructor(rootStore: IRootStore) {
    super();
    this.rootStore = rootStore;

    makeObservable(this, {
      disabledCreating: computed,

      removeToken: action.bound,
      createToken: action.bound,
      resetForm: action.bound
    });
  }

  get disabledCreating(): boolean {
    return (
      !this.nameField.value.trim() ||
      !this.tokenField.value.trim() ||
      this.nameField.isError ||
      this.tokenField.isError
    );
  }

  get loaded(): boolean {
    return this.tokens.loadingStage.successfullyLoaded;
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized;
  }

  async load(): Promise<BaseResponse> {
    if (this.tokens.loadingStage.isLoading) {
      return {
        isError: true
      };
    }

    this.tokens.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      tokens: AgentPlatformTokenServer[];
    }>(apiUrls.AGENT_PLATFORM_TOKENS_LIST);

    if (response.isError) {
      this.tokens.loadingStage.error();
    } else {
      this.tokens.fillByRawData({
        raw: response.data.tokens,
        normalizer: (token) => {
          const entity = AgentPlatformTokenModel.fromJson(
            token,
            this.rootStore
          );
          return {
            key: entity.id,
            entity
          };
        },
        initial: true
      });

      this.tokens.loadingStage.success();
    }

    return {
      isError: response.isError
    };
  }

  async createToken(): Promise<BaseResponse> {
    if (this.tokens.creatingStage.isLoading) {
      return { isError: true };
    }

    this.nameField.validate();
    this.tokenField.validate();

    if (this.disabledCreating) {
      return { isError: true };
    }

    this.tokens.creatingStage.loading();

    const response = await this.rootStore.networkStore.api<
      AgentPlatformTokenServer,
      CreateAgentPlatformTokenError
    >(apiUrls.AGENT_PLATFORM_TOKENS_CREATE, {
      method: 'POST',
      data: {
        name: this.nameField.value.trim(),
        token: this.tokenField.value.trim()
      },
      errorsMap: mapCreateAgentPlatformTokenErrorToMessage
    });

    if (response.isError) {
      this.tokens.creatingStage.error();
    } else {
      const tokenModel = AgentPlatformTokenModel.fromJson(
        response.data,
        this.rootStore
      );
      this.tokens.addEntity({
        entity: tokenModel,
        key: tokenModel.id,
        start: true
      });
      this.tokens.creatingStage.success();
    }

    return { isError: response.isError };
  }

  resetForm(): void {
    this.nameField.changeValue('');
    this.nameField.resetError();
    this.tokenField.changeValue('');
    this.tokenField.resetError();
  }

  async removeToken(id: string): Promise<BaseResponse> {
    const token = this.tokens.getEntity(id);

    if (!token) {
      return {
        isError: true
      };
    }

    const response = await token.remove();

    if (response.isError) {
      return {
        isError: true
      };
    }

    this.tokens.removeEntity(id);

    return {
      isError: false
    };
  }

  reset(): void {
    this.tokens.reset();
    this.resetForm();
  }
}
